import React, { useState, useRef } from 'react';
import { User, LogOut, Settings, Menu } from 'lucide-react';
import ThemeToggle from '../../MasterAdmin/ui/ThemeToggle';
import { useAuth } from '../../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface MarketingHeaderProps {
  title: string;
  onOpenMobileNav?: () => void;
}

const MarketingHeader: React.FC<MarketingHeaderProps> = ({ title, onOpenMobileNav }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="shrink-0 border-b border-gray-200 bg-white px-3 py-3 dark:border-gray-700 dark:bg-gray-800 sm:px-6 sm:py-4">
      <div className="flex items-center gap-2 sm:gap-3">
        <button
          type="button"
          onClick={() => onOpenMobileNav?.()}
          className="inline-flex shrink-0 rounded-lg p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 lg:hidden"
          aria-label="Open navigation menu"
        >
          <Menu className="h-6 w-6" />
        </button>

        <h1 className="min-w-0 flex-1 truncate text-lg font-semibold text-gray-800 dark:text-white sm:text-2xl">
          {title || 'Dashboard'}
        </h1>

        <div className="flex shrink-0 items-center gap-0.5 sm:gap-2 md:gap-3 lg:gap-4">
          <ThemeToggle />

          <div className="relative" ref={userMenuRef}>
            <div
              className="flex cursor-pointer items-center space-x-2"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900">
                <User className="h-5 w-5 text-primary-600 dark:text-primary-300" />
              </div>
              <div className="hidden md:block">
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {user?.name || 'Marketing User'}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {user?.email || 'marketing@vedika.com'}
                </div>
              </div>
            </div>

            {showUserMenu && (
              <div className="fixed right-2 top-16 z-50 w-[min(12rem,calc(100vw-1rem))] rounded-md border border-gray-200 bg-white py-2 shadow-lg dark:border-gray-700 dark:bg-gray-800 sm:absolute sm:right-0 sm:top-full sm:mt-2 sm:w-48">
                <button
                  onClick={() => alert('Profile functionality coming soon!')}
                  className="flex w-full items-center px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </button>
                <button
                  onClick={() => alert('Settings functionality coming soon!')}
                  className="flex w-full items-center px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </button>
                <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
                <button
                  onClick={() => void handleLogout()}
                  className="flex w-full items-center px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 dark:text-red-400 dark:hover:bg-gray-700"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default MarketingHeader;
